import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  HiTrendingUp,
  HiOfficeBuilding,
  HiViewGrid,
  HiSearch,
  HiRefresh,
  HiX,
  HiChevronUp,
  HiChevronDown,
  HiSwitchHorizontal,
  HiDownload,
} from "react-icons/hi";
import { selectGlobalDossier, selectGlobalEntreprise } from "../../slices/entrepriseGlobalSlice";
import {
  useGetTopVenteSyntheseQuery,
  useGetTopVenteDetailQuery,
} from "../../slices/topVentesApiSlice";
import { fmtQty, fmtFranc, fmtPct, fmtCoef } from "../../utils/format";
import { BASE_URL, TOP_VENTES_URL } from "../../constants";
import Modal from "../../components/ui/Modal/Modal";
import "./AdminTopVentesScreen.css";

// En-tête de colonne triable. `title` = infobulle explicative au survol.
const Th = ({ label, field, sort, dir, onSort, num, title }) => (
  <th className={num ? "num" : ""} onClick={() => onSort(field)} title={title}>
    {label}
    {title && <span className="tv-aide">?</span>}
    {sort === field && (
      <span className="arrow">{dir === "asc" ? <HiChevronUp /> : <HiChevronDown />}</span>
    )}
  </th>
);

// Infobulles des colonnes de rentabilité. Le texte dit la FORMULE, pas
// seulement le nom : marque et coefficient décrivent la même marge, l'une
// rapportée au prix de vente, l'autre au prix de revient — c'est la confusion
// la plus fréquente.
const AIDE_MARQUE =
  "Taux de marque HT = (prix de vente HT − prix de revient) / prix de vente HT × 100.\n" +
  "Exemple : acheté 600 F, vendu 1 000 F HT → (1000 − 600) / 1000 = 40 %.\n" +
  "Champs article.dbf : PVTE (vente HT) et PREV (revient).\n" +
  "Vide (—) si le prix de revient ou le prix de vente est à 0.";

const AIDE_COEF =
  "Coefficient multiplicateur = prix de vente HT / prix de revient.\n" +
  "Exemple : acheté 600 F, vendu 1 000 F HT → 1000 / 600 = 1,67.\n" +
  "Même marge que la colonne Marque, exprimée autrement : 40 % de marque = coef 1,67.\n" +
  "Vide (—) si le prix de revient ou le prix de vente est à 0.";

const AIDE_MARQUE_MOY =
  "Marque moyenne du groupe, PONDÉRÉE PAR LE CA (et non moyenne des marques article par article) :\n" +
  "(Σ CA − Σ coût) / Σ CA × 100, sur les 12 derniers mois.\n" +
  "Les articles sans prix de revient sont exclus des deux sommes.";

const AIDE_COEF_MOY =
  "Coefficient moyen du groupe = Σ CA / Σ coût sur les 12 derniers mois,\n" +
  "pondéré par les ventes (et non moyenne des coefficients article par article).\n" +
  "Les articles sans prix de revient sont exclus des deux sommes.";

// ════════════════════════════════════════════════════════════════════════════
const AdminTopVentesScreen = () => {
  const dossier = useSelector(selectGlobalDossier) || "";
  const entreprise = useSelector(selectGlobalEntreprise);

  const [groupBy, setGroupBy] = useState("fournisseur");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("caAnnee");
  const [dir, setDir] = useState("desc");
  const [drill, setDrill] = useState(null); // { type, code, label }

  const { data, isLoading, isFetching, error, refetch } =
    useGetTopVenteSyntheseQuery({ nomDossierDBF: dossier, groupBy }, { skip: !dossier });

  const onSort = (field) => {
    if (sort === field) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(field);
      setDir("desc");
    }
  };

  const lignes = useMemo(() => {
    let rows = data?.lignes || [];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(
        (l) =>
          String(l.code).toLowerCase().includes(s) ||
          (l.label || "").toLowerCase().includes(s),
      );
    }
    const sorted = [...rows].sort((a, b) => {
      const av = a[sort] ?? 0;
      const bv = b[sort] ?? 0;
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv)) * (dir === "asc" ? 1 : -1);
      }
      return (av - bv) * (dir === "asc" ? 1 : -1);
    });
    return sorted;
  }, [data, search, sort, dir]);

  if (!dossier) {
    return (
      <div className="tv-wrap">
        <div className="tv-head">
          <h1>
            <HiTrendingUp /> Top Ventes
          </h1>
        </div>
        <div className="tv-empty">
          Sélectionnez une société dans l'en-tête pour afficher le classement des ventes.
        </div>
      </div>
    );
  }

  return (
    <div className="tv-wrap">
      <div className="tv-head">
        <h1>
          <HiTrendingUp /> Top Ventes
        </h1>
        <div className="tv-soc">
          Société : <b>{entreprise?.nomComplet || dossier}</b>
        </div>
      </div>

      <div className="tv-toolbar">
        <div className="tv-seg">
          <button
            className={groupBy === "fournisseur" ? "active" : ""}
            onClick={() => setGroupBy("fournisseur")}
          >
            <HiOfficeBuilding style={{ verticalAlign: "-2px" }} /> Par fournisseur
          </button>
          <button
            className={groupBy === "rayon" ? "active" : ""}
            onClick={() => setGroupBy("rayon")}
          >
            <HiViewGrid style={{ verticalAlign: "-2px" }} /> Par rayon
          </button>
        </div>
        <div style={{ position: "relative" }}>
          <HiSearch
            style={{ position: "absolute", left: 10, top: 10, color: "#94a3b8" }}
          />
          <input
            className="tv-input"
            style={{ paddingLeft: 32 }}
            placeholder={`Rechercher un ${groupBy}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="tv-btn" onClick={() => refetch()} disabled={isFetching}>
          <HiRefresh /> Rafraîchir
        </button>
        <div className="tv-spacer" />
        <span className="tv-soc">
          Classement basé sur les ventes des 12 derniers mois (fiche article)
        </span>
      </div>

      {/* KPIs */}
      <div className="tv-kpis">
        <div className="tv-kpi">
          <div className="lbl">CA annuel total (estimé)</div>
          <div className="val">{fmtFranc(data?.totaux?.caAnnee)}</div>
        </div>
        <div className="tv-kpi">
          <div className="lbl">Ventes totales (qté)</div>
          <div className="val">{fmtQty(data?.totaux?.venteAnnee)}</div>
        </div>
        <div className="tv-kpi">
          <div className="lbl">{groupBy === "rayon" ? "Rayons" : "Fournisseurs"}</div>
          <div className="val">{fmtQty(data?.nbGroupes)}</div>
        </div>
      </div>

      {error && <div className="tv-empty">Erreur de chargement.</div>}

      <div className="tv-tablewrap">
        <table className="tv-table">
          <thead>
            <tr>
              <th style={{ cursor: "default" }}>#</th>
              <Th label="Code" field="code" sort={sort} dir={dir} onSort={onSort} />
              <Th
                label={groupBy === "rayon" ? "Rayon" : "Fournisseur"}
                field="label"
                sort={sort}
                dir={dir}
                onSort={onSort}
              />
              <Th label="Nb articles" field="nbArticles" sort={sort} dir={dir} onSort={onSort} num />
              <Th label="Ventes (qté/an)" field="venteAnnee" sort={sort} dir={dir} onSort={onSort} num />
              <Th label="CA annuel" field="caAnnee" sort={sort} dir={dir} onSort={onSort} num />
              <Th
                label="Marque moy. %"
                field="tauxMarqueMoy"
                sort={sort}
                dir={dir}
                onSort={onSort}
                num
                title={AIDE_MARQUE_MOY}
              />
              <Th
                label="Coef. moy."
                field="coefMoy"
                sort={sort}
                dir={dir}
                onSort={onSort}
                num
                title={AIDE_COEF_MOY}
              />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="tv-empty">
                  Chargement du classement…
                </td>
              </tr>
            ) : lignes.length === 0 ? (
              <tr>
                <td colSpan={8} className="tv-empty">
                  Aucune donnée.
                </td>
              </tr>
            ) : (
              lignes.map((l, i) => (
                <tr
                  key={`${l.code}-${i}`}
                  onClick={() => setDrill({ type: groupBy, code: l.code, label: l.label })}
                  title="Voir le détail des articles"
                >
                  <td className="tv-rank">{i + 1}</td>
                  <td>
                    <b>{l.code}</b>
                  </td>
                  <td className="wrap">{l.label || "—"}</td>
                  <td className="num">{fmtQty(l.nbArticles)}</td>
                  <td className="num">{fmtQty(l.venteAnnee)}</td>
                  <td className="num">
                    <b>{fmtFranc(l.caAnnee)}</b>
                  </td>
                  <td className="num">{fmtPct(l.tauxMarqueMoy)}</td>
                  <td className="num">{fmtCoef(l.coefMoy)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {drill && (
        <DetailModal dossier={dossier} drill={drill} onClose={() => setDrill(null)} />
      )}
    </div>
  );
};

// ── Modale détail (articles d'un fournisseur / rayon) ────────────────────────
const DetailModal = ({ dossier, drill, onClose }) => {
  const [sort, setSort] = useState("caAnnee");
  const [dir, setDir] = useState("desc");
  const [search, setSearch] = useState("");
  // "" = tous, "1" = uniquement les renvois, "0" = sans les renvois.
  const [renvoi, setRenvoi] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  // Export Excel du détail : mêmes filtres que l'affichage (recherche, renvois,
  // tri), mais TOUTES les lignes — le serveur n'applique pas la borne d'écran.
  const exporterExcel = async () => {
    setExportError("");
    setExporting(true);
    try {
      const params = new URLSearchParams({
        type: drill.type,
        code: String(drill.code),
        sort,
        dir,
      });
      if (renvoi !== "") params.set("renvoi", renvoi);
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(
        `${BASE_URL}${TOP_VENTES_URL}/${dossier}/export?${params}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        let msg = `Export échoué (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {
          /* réponse non-JSON */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `top_ventes_${drill.type}_${drill.code}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      setExportError(e.message || "Impossible d'exporter.");
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading, isFetching } = useGetTopVenteDetailQuery({
    nomDossierDBF: dossier,
    type: drill.type,
    code: drill.code,
    sort,
    dir,
    renvoi,
  });

  const onSort = (field) => {
    if (sort === field) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(field);
      setDir("desc");
    }
  };

  const articles = useMemo(() => {
    let rows = data?.articles || [];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((a) =>
        `${a.nart} ${a.design} ${a.refer} ${a.gencod}`.toLowerCase().includes(s),
      );
    }
    return rows;
  }, [data, search]);

  return (
    <Modal onClose={onClose} overlayClassName="tv-overlay" contentClassName="tv-modal">
      <h3>
        <HiTrendingUp /> {drill.type === "rayon" ? "Rayon" : "Fournisseur"}{" "}
        <span className="tv-badge">{drill.code}</span> {drill.label}
        <button className="tv-btn" style={{ marginLeft: "auto" }} onClick={onClose}>
          <HiX />
        </button>
      </h3>

      <div className="tv-toolbar">
        <div style={{ position: "relative" }}>
          <HiSearch style={{ position: "absolute", left: 10, top: 10, color: "#94a3b8" }} />
          <input
            className="tv-input"
            style={{ paddingLeft: 32 }}
            placeholder="Rechercher un article…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="tv-input"
          value={renvoi}
          onChange={(e) => setRenvoi(e.target.value)}
          title="Articles en renvoi fournisseur"
        >
          <option value="">Renvois : tous les articles</option>
          <option value="1">Uniquement les renvois</option>
          <option value="0">Sans les renvois</option>
        </select>
        <button
          className="tv-btn"
          onClick={exporterExcel}
          disabled={exporting || isLoading || (data?.total ?? 0) === 0}
          title="Exporter en Excel les articles affichés (mêmes filtres, toutes les lignes)"
        >
          <HiDownload /> {exporting ? "Export…" : "Exporter Excel"}
        </button>
        <div className="tv-spacer" />
        <span className="tv-soc">
          {exportError && (
            <span style={{ color: "#f87171" }}>{exportError} · </span>
          )}
          {data?.total ?? 0} article(s)
          {data?.nbRenvois > 0 && (
            <>
              {" · "}
              <span className="tv-renvoi-count">
                {data.nbRenvois} en renvoi
              </span>
            </>
          )}
          {isFetching ? " · …" : ""}
        </span>
      </div>

      <div className="tv-tablewrap" style={{ maxHeight: "62vh" }}>
        <table className="tv-table">
          <thead>
            <tr>
              <th style={{ cursor: "default" }}>Code</th>
              <th style={{ cursor: "default" }}>Désignation</th>
              {drill.type === "fournisseur" && (
                <th style={{ cursor: "default" }}>Rayon</th>
              )}
              <Th label="Ventes/an" field="venteAnnee" sort={sort} dir={dir} onSort={onSort} num />
              <Th label="CA annuel" field="caAnnee" sort={sort} dir={dir} onSort={onSort} num />
              <Th
                label="Marque %"
                field="tauxMarque"
                sort={sort}
                dir={dir}
                onSort={onSort}
                num
                title={AIDE_MARQUE}
              />
              <Th
                label="Coef."
                field="coef"
                sort={sort}
                dir={dir}
                onSort={onSort}
                num
                title={AIDE_COEF}
              />
              <Th label="Rupture (j)" field="jourRupture" sort={sort} dir={dir} onSort={onSort} num />
              <Th label="Stock" field="stockTotal" sort={sort} dir={dir} onSort={onSort} num />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={drill.type === "fournisseur" ? 9 : 8} className="tv-empty">
                  Chargement…
                </td>
              </tr>
            ) : articles.length === 0 ? (
              <tr>
                <td colSpan={drill.type === "fournisseur" ? 9 : 8} className="tv-empty">
                  Aucun article.
                </td>
              </tr>
            ) : (
              articles.map((a) => (
                <tr key={a.nart} style={{ cursor: "default" }}>
                  <td>
                    <b>{a.nart}</b>
                  </td>
                  <td className="wrap">
                    {a.design}
                    {a.renvoi && (
                      <span
                        className={`tv-tag-renvoi${a.renvoi.trouve ? "" : " orphelin"}`}
                        title={
                          a.renvoi.trouve
                            ? `Renvoie vers l'article ${a.renvoi.nart} — ${a.renvoi.design}`
                            : `Renvoie vers ${a.renvoi.ref}, introuvable dans article.dbf`
                        }
                      >
                        <HiSwitchHorizontal /> vers {a.renvoi.nart}
                      </span>
                    )}
                  </td>
                  {drill.type === "fournisseur" && (
                    <td className="wrap">{a.rayon || a.gism1 || "—"}</td>
                  )}
                  <td className="num">{fmtQty(a.venteAnnee)}</td>
                  <td className="num">{fmtFranc(a.caAnnee)}</td>
                  <td className="num">{fmtPct(a.tauxMarque)}</td>
                  <td className="num">{fmtCoef(a.coef)}</td>
                  <td className="num">{a.jourRupture}</td>
                  <td className="num">{fmtQty(a.stockTotal)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default AdminTopVentesScreen;
